
import React, { useState } from 'react';
import { CheckIcon, Calendar, Clock, DollarSign, Check, CalendarIcon, PlusIcon, MinusIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const pricingTiers = [
  {
    id: 'team',
    name: 'Team Tier',
    description: 'Perfect for small teams',
    basePrice: 30,
    icon: '🤖',
    iconColor: 'text-blue-500',
    popular: false,
    features: [
      'Single AI Agent',
      'Basic automations',
      '500 conversations/month',
      'Email support',
      'Knowledge base access',
    ],
    includeModels: 1,
    extraModelPrice: 5,
  },
  {
    id: 'business',
    name: 'Business Tier',
    description: 'For growing businesses',
    basePrice: 40,
    icon: '🚀',
    iconColor: 'text-purple-500',
    popular: true,
    features: [
      'Up to 3 AI Agents',
      'Advanced automations',
      '1,000 conversations/month',
      'Email & chat support',
      'Knowledge base integration',
    ],
    includeModels: 4,
    extraModelPrice: 5,
  },
  {
    id: 'enterprise',
    name: 'AI Agents Only',
    description: 'Customized for large teams',
    basePrice: null,
    icon: '🏢',
    iconColor: 'text-synthreo-green-neon',
    popular: false,
    features: [
      'Unlimited AI Agents',
      'Custom automations',
      'Unlimited conversations',
      'Dedicated support',
      'Advanced integrations',
    ],
    includeModels: -1,
    extraModelPrice: 0,
  }
];

const aiModels = [
  { id: 'gpt-azure', name: 'GPT (Azure w/ ZDR)', default: false },
  { id: 'gpt-openai', name: 'GPT (OpenAI Native)', default: false },
  { id: 'claude', name: 'Claude', default: false },
  { id: 'gemini', name: 'Gemini', default: false },
  { id: 'command-r', name: 'Command R', default: false },
  { id: 'mistral', name: 'Mistral', default: false },
  { id: 'llama', name: 'LLaMA', default: false },
];

const aiAgents = [
  { 
    id: 'hr-agent', 
    name: 'AI HR Agent', 
    price: 699, 
    description: 'Handle HR inquiries trained on your HRIS and policies' 
  },
  { 
    id: 'web-chatbot', 
    name: 'AI Web Chatbot Agent', 
    price: 199, 
    description: 'Customer-facing website agent trained on your site and sales collateral' 
  },
  { 
    id: 'multi-llm', 
    name: 'AI Multi-LLM Conversation Agent', 
    price: 699, 
    description: 'Query 4 LLMs at once summarized for accuracy' 
  },
  { 
    id: 'custom-agent', 
    name: 'AI Custom Agent', 
    price: 699, 
    description: 'Tailored to your specific needs' 
  },
];

const ThreoAIPricingTiers = () => {
  const [selectedPlan, setSelectedPlan] = useState('team');
  const [userCount, setUserCount] = useState(5);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedModels, setSelectedModels] = useState<string[]>(['gpt-azure']);
  const [agentQuantities, setAgentQuantities] = useState<Record<string, number>>({
    'hr-agent': 0,
    'web-chatbot': 0,
    'multi-llm': 0,
    'custom-agent': 0,
  });
  
  const selectedTier = pricingTiers.find(tier => tier.id === selectedPlan) || pricingTiers[0];
  
  const isAgentsOnlyPlan = selectedPlan === 'enterprise';
  
  const basePrice = selectedTier.basePrice ? selectedTier.basePrice * userCount : 0;
  
  const extraModelCost = (() => {
    if (selectedPlan === 'enterprise') return 0;
    
    const includedModelsCount = selectedTier.includeModels || 0;
    const extraModelsCount = Math.max(0, selectedModels.length - includedModelsCount);
    return extraModelsCount * (selectedTier.extraModelPrice || 0);
  })();
  
  const agentCost = Object.entries(agentQuantities).reduce((total, [agentId, quantity]) => {
    const agent = aiAgents.find(a => a.id === agentId);
    return total + (agent?.price || 0) * quantity;
  }, 0);

  const totalSelectedAgents = Object.values(agentQuantities).reduce((sum, qty) => sum + qty, 0);
  
  const totalBeforeDiscount = basePrice + extraModelCost + agentCost;
  const discount = billingCycle === 'annual' ? totalBeforeDiscount * 0.1 : 0;
  
  const finalPrice = totalBeforeDiscount - discount;
  const formattedFinalPrice = finalPrice.toFixed(2);
  const annualTotal = (finalPrice * 12).toFixed(2);
  
  const hoursPerUserPerMonth = selectedTier.basePrice > 0 ? 2 : 0;
  const totalHoursPerMonth = userCount * hoursPerUserPerMonth;
  const totalHoursPerYear = totalHoursPerMonth * 12;
  
  const hourlyRate = 50;
  const monthlyCostSavings = totalHoursPerMonth * hourlyRate;
  const yearlyCostSavings = monthlyCostSavings * 12;

  const handleModelToggle = (modelId: string) => {
    if (isAgentsOnlyPlan) return;
    
    setSelectedModels(prevSelectedModels => {
      if (prevSelectedModels.includes(modelId)) {
        return prevSelectedModels.filter(id => id !== modelId);
      } else {
        return [...prevSelectedModels, modelId];
      }
    });
  };

  const addModel = (modelId: string) => {
    if (isAgentsOnlyPlan) return;
    
    if (!selectedModels.includes(modelId)) {
      setSelectedModels([...selectedModels, modelId]);
    }
  };
  
  const removeModel = (modelId: string) => {
    if (isAgentsOnlyPlan) return;
    
    if (selectedModels.length <= 1) {
      return;
    }
    setSelectedModels(selectedModels.filter(id => id !== modelId));
  };
  
  const handleAgentCountChange = (agentId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue >= 0 && newValue <= 100) {
      setAgentQuantities(prev => ({
        ...prev,
        [agentId]: newValue
      }));
    } else if (e.target.value === '') {
      setAgentQuantities(prev => ({
        ...prev,
        [agentId]: 0
      }));
    }
  };

  const incrementAgentQuantity = (agentId: string) => {
    setAgentQuantities(prev => ({
      ...prev,
      [agentId]: Math.min(100, (prev[agentId] || 0) + 1)
    }));
  };

  const decrementAgentQuantity = (agentId: string) => {
    setAgentQuantities(prev => ({
      ...prev,
      [agentId]: Math.max(0, (prev[agentId] || 0) - 1)
    }));
  };

  const handleUserCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    if (!isNaN(newValue) && newValue > 0 && newValue <= 1000) {
      setUserCount(newValue);
    } else if (e.target.value === '') {
      setUserCount(0);
    }
  };

  const incrementUserCount = () => {
    if (userCount < 1000) {
      setUserCount(prevCount => prevCount + 1);
    }
  };

  const decrementUserCount = () => {
    if (userCount > 1) {
      setUserCount(prevCount => prevCount - 1);
    }
  };

  return (
    <section className="py-2 md:py-6">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="w-full lg:w-7/12">
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <span className="bg-synthreo-blue-light/20 text-synthreo-blue-light w-7 h-7 rounded-full flex items-center justify-center mr-2">1</span>
                Select Your Plan
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {pricingTiers.map((tier) => (
                  <div 
                    key={tier.id}
                    onClick={() => setSelectedPlan(tier.id)}
                    className={`border rounded-lg p-6 cursor-pointer transition-all ${
                      tier.id === selectedPlan 
                        ? 'border-2 border-synthreo-blue-medium shadow-lg bg-synthreo-black-light' 
                        : 'border-gray-700 hover:border-synthreo-blue-light'
                    }`}
                  >
                    <div className="flex justify-center mb-3">
                      <div className={`text-2xl ${tier.iconColor}`}>
                        {tier.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-center mb-1">{tier.name}</h3>
                    <p className="text-sm text-gray-400 text-center mb-4">{tier.description}</p>
                    <div className="text-center">
                      {tier.basePrice !== null ? (
                        <>
                          <div className="text-xl font-bold mb-1">
                            ${tier.basePrice}
                          </div>
                          <div className="text-sm text-gray-400">/ user / month</div>
                        </>
                      ) : (
                        <div className="text-xl font-bold mb-1">Custom Pricing</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <span className="bg-synthreo-blue-light/20 text-synthreo-blue-light w-7 h-7 rounded-full flex items-center justify-center mr-2">2</span>
                  Choose Billing Cycle
                </h2>

                <div className="bg-synthreo-black-light border border-gray-700 rounded-lg p-4 flex justify-center items-center">
                  <div className="flex bg-synthreo-green-neon/5 rounded-full p-1.5">
                    <button
                      onClick={() => setBillingCycle('monthly')}
                      className={`py-2 px-6 rounded-full flex items-center justify-center transition-all ${
                        billingCycle === 'monthly' 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-transparent text-gray-400'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setBillingCycle('annual')}
                      className={`py-2 px-6 rounded-full flex items-center justify-center transition-all ${
                        billingCycle === 'annual' 
                          ? 'bg-emerald-500 text-white' 
                          : 'bg-transparent text-gray-400'
                      }`}
                    >
                      Annual (Save $)
                    </button>
                  </div>
                </div>

                {billingCycle === 'annual' && (
                  <div className="bg-synthreo-green-neon/10 border border-synthreo-green-neon/50 text-synthreo-green-neon p-3 rounded-md mt-4 mb-4">
                    You're saving with annual billing!
                  </div>
                )}
              </div>

              <div className={`${isAgentsOnlyPlan ? 'opacity-50 pointer-events-none' : ''}`}>
                <h2 className="text-xl font-bold mb-4 flex items-center">
                  <span className="bg-synthreo-blue-light/20 text-synthreo-blue-light w-7 h-7 rounded-full flex items-center justify-center mr-2">3</span>
                  Number of Users
                </h2>

                <div className="bg-synthreo-black-light border border-gray-700 rounded-lg p-4 flex justify-center items-center h-[74px]">
                  <div className="flex items-center gap-6">
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={decrementUserCount}
                      className="h-10 w-10 rounded-md"
                      disabled={userCount <= 1}
                    >
                      <MinusIcon className="h-4 w-4" />
                    </Button>
                    
                    <Input
                      type="number"
                      min="1"
                      max="1000"
                      value={userCount}
                      onChange={handleUserCountChange}
                      className="w-20 text-center text-lg font-bold px-3 py-2"
                    />
                    
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={incrementUserCount}
                      className="h-10 w-10 rounded-md"
                      disabled={userCount >= 1000}
                    >
                      <PlusIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <span className="bg-synthreo-blue-light/20 text-synthreo-blue-light w-7 h-7 rounded-full flex items-center justify-center mr-2">4</span>
                Select AI Agents {!isAgentsOnlyPlan && "(Optional)"}
              </h2>

              <div className="bg-synthreo-black-light p-4 border border-gray-700 rounded-lg mb-6">
                <div className="text-sm mb-3">
                  <p>Optionally select AI Agents to enhance your capabilities</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aiAgents.map((agent) => (
                    <div 
                      key={agent.id}
                      className="border p-4 rounded-lg border-gray-700 bg-synthreo-black"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-medium">{agent.name}</div>
                          <div className="text-xs text-gray-300 mt-1">{agent.description}</div>
                          <div className="text-sm text-gray-400 mt-2">${agent.price}/month per agent</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="text-sm">Quantity:</div>
                        <div className="flex items-center gap-3">
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => decrementAgentQuantity(agent.id)}
                            className="h-8 w-8 rounded-md"
                            disabled={agentQuantities[agent.id] <= 0}
                          >
                            <MinusIcon className="h-3 w-3" />
                          </Button>
                          
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={agentQuantities[agent.id] || 0}
                            onChange={(e) => handleAgentCountChange(agent.id, e)}
                            className="w-20 text-center text-sm px-2 py-1"
                          />
                          
                          <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => incrementAgentQuantity(agent.id)}
                            className="h-8 w-8 rounded-md"
                          >
                            <PlusIcon className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      {agentQuantities[agent.id] > 0 && (
                        <div className="mt-2 text-right text-sm text-amber-300">
                          Total: ${(agent.price * agentQuantities[agent.id]).toFixed(2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {totalSelectedAgents > 0 && (
                  <div className="mt-4 text-amber-300 text-sm">
                    You've selected {totalSelectedAgents} agent(s) for a total of ${agentCost.toFixed(2)}.
                  </div>
                )}
              </div>
            </div>

            <div className={`${isAgentsOnlyPlan ? 'opacity-50 pointer-events-none' : ''}`}>
              <h2 className="text-xl font-bold mb-4 flex items-center">
                <span className="bg-synthreo-blue-light/20 text-synthreo-blue-light w-7 h-7 rounded-full flex items-center justify-center mr-2">5</span>
                Select AI Models
              </h2>

              <div className="bg-synthreo-black-light p-4 border border-gray-700 rounded-lg">
                <div className="text-sm mb-3">
                  {selectedPlan === 'team' ? (
                    <p>Team tier includes <strong>1 model</strong>. Additional models are <strong>$5 per model</strong>.</p>
                  ) : selectedPlan === 'business' ? (
                    <p>Business tier includes <strong>up to 4 models</strong>. Additional models are <strong>$5 per model</strong>.</p>
                  ) : selectedPlan === 'enterprise' ? (
                    <p>AI Agents Only tier includes <strong>unlimited AI models</strong> at no extra cost.</p>
                  ) : (
                    <p>Select a plan to configure AI models.</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aiModels.map((model) => (
                    <div 
                      key={model.id}
                      className={`border p-3 rounded-lg flex items-center justify-between ${
                        selectedModels.includes(model.id) 
                          ? 'border-synthreo-blue-light bg-synthreo-blue-darkest/30' 
                          : 'border-gray-700'
                      } ${model.default || isAgentsOnlyPlan ? 'relative' : ''}`}
                    >
                      <div>
                        <div className="font-medium text-sm">{model.name}</div>
                      </div>
                      
                      <button
                        onClick={() => handleModelToggle(model.id)}
                        disabled={model.default || isAgentsOnlyPlan}
                        className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          selectedModels.includes(model.id)
                            ? 'bg-synthreo-blue-light text-white'
                            : 'bg-gray-700 text-gray-300'
                        } ${model.default || isAgentsOnlyPlan ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-opacity-80'}`}
                      >
                        {selectedModels.includes(model.id) ? <Check size={14} /> : '+'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-5/12">
            <div className="lg:sticky lg:top-24 transition-all duration-200">
              <div>
                <Card className="bg-white text-black rounded-lg overflow-hidden mb-6">
                  <CardContent className="p-6 pb-8">
                    <h2 className="text-xl font-bold mb-6">Your Pricing Summary</h2>
                    
                    {isAgentsOnlyPlan ? (
                      <>
                        {totalSelectedAgents > 0 ? (
                          <>
                            <div className="flex justify-between mb-2">
                              <span>AI Agents ({totalSelectedAgents})</span>
                              <span>${agentCost.toFixed(2)}</span>
                            </div>
                            
                            <div className="flex justify-between mb-2 text-lg font-bold">
                              <span>Monthly Agent Costs</span>
                              <span>${agentCost.toFixed(2)}</span>
                            </div>
                          </>
                        ) : (
                          <div className="text-center p-6 text-gray-600">
                            <p className="mb-4">Select AI agents to see pricing</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between mb-2">
                          <span>Base plan</span>
                          <span>${basePrice.toFixed(2)}</span>
                        </div>
                        
                        {extraModelCost > 0 && (
                          <div className="flex justify-between mb-2">
                            <span>Additional models ({selectedModels.length - (selectedTier?.includeModels || 0)})</span>
                            <span>${extraModelCost.toFixed(2)}</span>
                          </div>
                        )}

                        {agentCost > 0 && (
                          <div className="flex justify-between mb-2">
                            <span>AI Agents ({totalSelectedAgents})</span>
                            <span>${agentCost.toFixed(2)}</span>
                          </div>
                        )}
                        
                        <div className="flex justify-between mb-6">
                          <span>Add-ons</span>
                          <span>$0.00</span>
                        </div>
                        
                        <div className="flex justify-between mb-2">
                          <span className="text-lg font-medium">Monthly Price</span>
                          <span className="text-lg font-medium line-through text-gray-500">${totalBeforeDiscount.toFixed(2)}</span>
                        </div>
                        
                        {discount > 0 && (
                          <div className="flex justify-between mb-2 text-synthreo-green-neon">
                            <span>Total discount</span>
                            <span>-${discount.toFixed(2)}</span>
                          </div>
                        )}
                        
                        <div className="flex justify-between mb-2 text-lg font-bold">
                          <span>New Monthly Price</span>
                          <span>${formattedFinalPrice}</span>
                        </div>
                        
                        <div className="flex justify-between mb-6">
                          <span>Annual total</span>
                          <span>${annualTotal}</span>
                        </div>
                        
                        <div className="text-sm text-gray-500 mb-8">
                          Based on {userCount} users per year
                        </div>
                        
                        <div className="mb-6">
                          <div className="flex items-center mb-2">
                            <Clock className="mr-2 text-blue-500" size={20} />
                            <h3 className="font-bold">Projected Time Savings</h3>
                          </div>
                          
                          <div className="text-sm mb-2">
                            Base plan: {hoursPerUserPerMonth} hours per user per month
                          </div>
                          
                          <div className="text-synthreo-green-neon font-medium">
                            Total estimated time savings:
                            <div className="text-lg">{totalHoursPerMonth.toFixed(2)} hours per month</div>
                            <div className="text-lg">{totalHoursPerYear.toFixed(2)} hours per year</div>
                          </div>
                        </div>
                        
                        <div className="mb-8">
                          <div className="flex items-center mb-2">
                            <DollarSign className="mr-2 text-synthreo-green-neon" size={20} />
                            <h3 className="font-bold">Projected Cost Savings</h3>
                          </div>
                          
                          <div className="text-sm mb-2">
                            Average cost per hour:
                            <div className="font-medium">${hourlyRate}</div>
                          </div>
                          
                          <div className="text-synthreo-green-neon font-medium">
                            Total estimated cost savings:
                            <div className="text-lg">${monthlyCostSavings.toFixed(2)} per month</div>
                            <div className="text-lg">${yearlyCostSavings.toFixed(2)} per year</div>
                          </div>
                        </div>
                      </>
                    )}
                    
                    <Button 
                      className="w-full py-6 btn-glow"
                      asChild
                    >
                      <Link to="/auth/signup">
                        Get Started
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ThreoAIPricingTiers;
